<div class="dash-shell ks-create">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      @if (entity(); as e) {
        <a class="page-head__crumb" [routerLink]="['/entity', e.short_id, 'dashboard']">{{
          e.name
        }}</a>
        <span class="page-head__sep" aria-hidden="true">/</span>
      }
      <a class="page-head__crumb" [routerLink]="['/entity', entityId, 'df']">Debtors Finance</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page">Detail</span>
    </nav>
  </header>

  @if (isLoading()) {
    <div class="loading-state" aria-label="Loading facility">
      <span class="sg-skeleton sg-skeleton--heading sg-skeleton--w40"></span>
      <span class="sg-skeleton sg-skeleton--text" style="width: 25%"></span>
      <mat-card appearance="outlined" style="margin-top: var(--space-6)">
        <mat-card-content>
          <span
            class="sg-skeleton sg-skeleton--text"
            style="width: 30%; margin-bottom: var(--space-4); display: block"
          ></span>
          <span class="sg-skeleton sg-skeleton--text sg-skeleton--w100"></span>
          <span class="sg-skeleton sg-skeleton--text sg-skeleton--w80"></span>
          <span class="sg-skeleton sg-skeleton--text sg-skeleton--w60"></span>
        </mat-card-content>
      </mat-card>
    </div>
  } @else if (loadError()) {
    <div class="sg-empty sg-empty--error sg-empty--bordered">
      <app-icon name="x-circle" [size]="40" />
      <p class="sg-empty__title">Couldn't load this facility</p>
      <p class="sg-empty__desc">Something went wrong on our side.</p>
      <button mat-stroked-button type="button" (click)="reload()">
        <app-icon name="refresh" [size]="18" /> Try again
      </button>
    </div>
  } @else if (facility(); as f) {
    @if (serverError(); as error) {
      <div class="ks-banner ks-banner--error" role="alert">
        <app-icon name="x-circle" [size]="18" />
        <span>{{ error }}</span>
      </div>
    }

    <div class="detail-header">
      <div class="page-head__titles--icon">
        <span class="page-head__icon" aria-hidden="true"
          ><app-icon name="trending-up" [size]="24"
        /></span>
        <h1 class="detail-header__title">Debtors Finance</h1>
        @if (f.requested_limit) {
          <p class="detail-header__amount">Requested limit: {{ f.requested_limit | zar }}</p>
        }
        @if (f.dealt_with_by) {
          <p class="detail-header__amount" data-testid="dealt-with-by">
            Dealt with at CapX by {{ f.dealt_with_by }}
          </p>
        }
      </div>
      <div class="detail-header__right">
        <div class="detail-header__chips">
          <app-status-chip [status]="f.df_status" />
          <app-status-chip [status]="f.application_stage" />
          @if (f.facility_subtype) {
            <app-status-chip [status]="f.facility_subtype" />
          }
        </div>
        @if (isDraft()) {
          <a
            mat-flat-button
            color="primary"
            href="#capture-form"
            aria-label="Continue completing your Debtors Finance application"
            data-testid="resume-draft-btn"
          >
            <app-icon name="arrow-right" [size]="18" />
            <span>Continue application</span>
          </a>
        }
      </div>
    </div>

    @if (isDraft()) {
      <div class="draft-banner" role="note">
        <app-icon name="pencil" [size]="18" aria-hidden="true" />
        <p class="draft-banner__text" data-testid="draft-banner-text">
          Started {{ f.created_at | ago }} — still a draft. Fill in your details and upload your
          documents, then submit when you're ready.
        </p>
      </div>
    }

    <!-- Assessment progress (BRE-200 Screen 6.2) — under review only.
         A rolled-up percentage; individual onboarding conditions are Ops
         detail and never surface to the client (N4). -->
    @if (showOnboardingProgress()) {
      <mat-card appearance="outlined" class="ks-card">
        <mat-card-content>
          <div class="ks-progress">
            <div class="ks-progress__head">
              <h2 class="ks-card__title">Assessment</h2>
              <span class="ks-progress__pct" data-testid="onboarding-pct"
                >{{ onboardingPercent() }}% complete</span
              >
            </div>
            <mat-progress-bar
              mode="determinate"
              [value]="onboardingPercent()"
              aria-label="Assessment completeness"
            />
            <p class="ks-progress__note">
              We're reviewing your facility. This updates as each check clears — in the meantime you
              can start onboarding your debtors to speed things up.
            </p>
          </div>
        </mat-card-content>
      </mat-card>
    }

    <!-- Client confirmation (BRE-200 DF-OB-06).
         A hard gate on activation: until the agreement is signed — or Ops
         records a waiver — the facility cannot go live. DF-OB-07 (cession) is
         an Ops confirmation and never appears on this client surface. -->
    @if (showAgreementConfirm()) {
      <mat-card appearance="outlined" class="ks-card">
        <mat-card-content>
          <div class="ks-card__head">
            <h2 class="ks-card__title">Your confirmations</h2>
          </div>
          <p class="ks-confirm__intro">
            @if (agreementSigned()) {
              Thank you — your facility agreement is signed.
            } @else {
              Before your facility can go live, please sign the facility agreement.
            }
          </p>

          <ul class="ks-confirm__list">
            <li class="ks-confirm__item" data-testid="confirm-df-agreement">
              <div class="ks-confirm__text">
                <span class="ks-confirm__label">Facility agreement</span>
                <span class="ks-confirm__desc">
                  Confirm you have read and signed the Debtors Finance facility agreement.
                </span>
              </div>

              @if (agreementSigned()) {
                <!-- Icon + text, never colour alone (a11y). -->
                <span class="ks-confirm__done" data-testid="confirmed-df-agreement">
                  <app-icon name="check" [size]="16" />
                  <span>Signed {{ agreementSignedAt() | date: 'medium' | dash }}</span>
                </span>
              } @else {
                <button
                  mat-stroked-button
                  type="button"
                  [disabled]="isSigningAgreement()"
                  data-testid="confirm-btn-df-agreement"
                  (click)="signFacilityAgreement()"
                >
                  {{ isSigningAgreement() ? 'Signing…' : 'Sign agreement' }}
                </button>
              }
            </li>
          </ul>
        </mat-card-content>
      </mat-card>
    }

    <!-- Facility details -->
    <mat-card appearance="outlined" class="ks-card">
      <mat-card-content>
        <div class="ks-card__head">
          <h2 class="ks-card__title">Facility details</h2>
        </div>
        <dl class="facts">
          <div class="facts__row">
            <dt>Business</dt>
            <dd>{{ f.entity_name }}</dd>
          </div>
          <div class="facts__row">
            <dt>Intent</dt>
            <dd>{{ f.df_intent | dash }}</dd>
          </div>
          <div class="facts__row">
            <dt>Submitted</dt>
            <dd>{{ f.submitted_at | date: 'medium' | dash }}</dd>
          </div>
        </dl>
      </mat-card-content>
    </mat-card>

    <!-- Capture form -->
    @if (canCapture()) {
      <mat-card appearance="outlined" id="capture-form" class="ks-card">
        <mat-card-content>
          <div class="ks-card__head">
            <h2 class="ks-card__title">Financial details</h2>
            <p class="ks-card__hint">
              Add what you can now — you can save and come back to the rest before you submit. CapX
              verifies your business details for you; you don't need to do anything here.
            </p>
          </div>
          <form [formGroup]="captureForm" (ngSubmit)="saveCapture()" class="form">
            <mat-form-field appearance="outline">
              <mat-label>Facility intent</mat-label>
              <textarea matInput formControlName="df_intent" rows="3"></textarea>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Requested limit (ZAR)</mat-label>
              <input matInput type="number" formControlName="requested_limit" min="1" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Trading history (months)</mat-label>
              <input matInput type="number" formControlName="trading_history_months" min="0" />
            </mat-form-field>

            <div class="form__actions">
              <button
                mat-stroked-button
                type="submit"
                [disabled]="isSaving()"
                data-testid="save-capture-btn"
              >
                @if (isSaving()) {
                  <mat-spinner diameter="20" />
                } @else {
                  <span>Save details</span>
                }
              </button>
            </div>
          </form>
        </mat-card-content>
      </mat-card>
    }

    <!-- FLOW-125 — the Ops ask, above the upload panel. Same rule as the KS lane:
         gated on the stage as well as the message, because re-submitting flips the
         stage back while the message column keeps its value.
         ⚠️ This does NOT replace the status timeline below — that stays the history
         of status changes and keeps rendering `note`. This is the current ask,
         placed where the client acts on it. -->
    @if (f.application_stage === 'MORE_INFO_REQUIRED' && f.client_info_request_message) {
      <div class="info-request-banner" role="note" data-testid="info-request-banner">
        <app-icon name="inbox" [size]="18" aria-hidden="true" />
        <div class="info-request-banner__body">
          <p class="info-request-banner__title">More information needed</p>
          <p class="info-request-banner__text">{{ f.client_info_request_message }}</p>
        </div>
      </div>
    }

    <!-- Documents -->
    @if (canCapture() || f.documents.length > 0) {
      <mat-card appearance="outlined" class="ks-card">
        <mat-card-content>
          <div class="ks-card__head">
            <h2 class="ks-card__title">Supporting documents</h2>
            @if (canCapture()) {
              <p class="ks-card__hint">
                Drop files into the blocks below — you can add several at once. We check each one as
                it uploads and give you a quick signal on whether it looks right (max 100&nbsp;MB
                each, PDF or images).
              </p>
            }
          </div>
          <app-doc-upload-panel
            [docTypes]="DOC_TYPES"
            [shortId]="f.short_id"
            [editable]="canCapture()"
            [initialDocs]="initialDocs()"
            [uploadFn]="uploadDoc"
            [deleteFn]="deleteDoc"
            [fetchDocsFn]="fetchDocs"
            (inFlightChange)="anyInFlight.set($event)"
          />
        </mat-card-content>
      </mat-card>
    }

    <!-- Submit / respond -->
    @if (canSubmit()) {
      <!--
        Sign-up question since 2026-09-22. Shown here only as the fallback: no
        standing consent, or the consent on this facility was withdrawn.
      -->
      @if (!isMoreInfo() && showAiConsentBox()) {
        <div class="df-ai-consent">
          <app-ai-consent-checkbox [checked]="aiConsent()" (changed)="aiConsent.set($event)" />
        </div>
      }
      <!--
        FLOW-290 — the promise now lives inside the control that honours it.
        Outside the !isMoreInfo() branch on purpose: a facility bounced back for
        more information still has a live consent, and the right to withdraw it
        does not pause while Ops waits.
      -->
      <app-ai-consent-withdraw
        [state]="aiConsentState()"
        [confirm]="withdrawAiConsent"
        (withdrawn)="onAiConsentWithdrawn()"
      />
      <div class="ks-actions ks-actions--end">
        @if (isDraft()) {
          <button
            mat-button
            type="button"
            class="ks-actions__withdraw"
            (click)="withdrawDraft()"
            [disabled]="isWithdrawing()"
            data-testid="withdraw-btn"
          >
            {{ isWithdrawing() ? 'Withdrawing…' : 'Withdraw application' }}
          </button>
        }
        <button
          mat-flat-button
          color="primary"
          type="button"
          (click)="submitOrRespond()"
          [disabled]="isSubmitting() || anyInFlight() || (!isMoreInfo() && !aiConsentOk())"
          data-testid="submit-btn"
        >
          @if (isSubmitting()) {
            <mat-spinner diameter="20" />
          } @else if (anyInFlight()) {
            <mat-spinner diameter="20" />
            <span>Checking documents…</span>
          } @else {
            <app-icon name="check" [size]="18" />
            <span>{{
              f.application_stage === 'MORE_INFO_REQUIRED'
                ? 'Respond with updated information'
                : 'Submit application'
            }}</span>
          }
        </button>
      </div>
    }

    <!-- Status timeline -->
    @if (f.status_events.length > 0) {
      <mat-card appearance="outlined">
        <mat-card-content>
          <h2 class="section-title">Status history</h2>
          <ol class="timeline">
            @for (event of f.status_events; track $index) {
              <li class="timeline__item">
                <app-status-chip [status]="event.to_status" />
                <span class="timeline__meta">
                  {{ event.created_at | date: 'medium' }}
                  @if (event.actor_name) {
                    · {{ event.actor_name }}
                  }
                </span>
                @if (event.note) {
                  <p class="timeline__note">{{ event.note }}</p>
                }
              </li>
            }
          </ol>
        </mat-card-content>
      </mat-card>
    }

    <!-- Debtors & Invoices — only for ACTIVE facilities -->
    @if (isActive()) {
      <mat-card appearance="outlined">
        <mat-card-content>
          <h2 class="section-title">Debtors</h2>

          @if (debtors().length === 0) {
            <div class="sg-empty">
              <app-icon name="building" [size]="40" />
              <p class="sg-empty__title">No debtors yet</p>
              <p class="sg-empty__desc">Add a debtor below to get started.</p>
            </div>
          } @else {
            <table mat-table [dataSource]="debtors()" class="data-table">
              <ng-container matColumnDef="name">
                <th mat-header-cell *matHeaderCellDef>Debtor</th>
                <td mat-cell *matCellDef="let d">{{ d.debtor.legal_name }}</td>
              </ng-container>
              <ng-container matColumnDef="reg_no">
                <th mat-header-cell *matHeaderCellDef>Reg no</th>
                <td mat-cell *matCellDef="let d">{{ d.debtor.registration_number | dash }}</td>
              </ng-container>
              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Status</th>
                <td mat-cell *matCellDef="let d">
                  <app-status-chip [status]="d.approval_status" />
                </td>
              </ng-container>
              <tr mat-header-row *matHeaderRowDef="['name', 'reg_no', 'status']"></tr>
              <tr mat-row *matRowDef="let d; columns: ['name', 'reg_no', 'status']"></tr>
            </table>
          }

          <form [formGroup]="addDebtorForm" (ngSubmit)="addDebtor()" class="inline-form">
            <h3 class="sub-section-title">Add debtor</h3>
            <mat-form-field appearance="outline">
              <mat-label>Company name</mat-label>
              <input matInput formControlName="name" data-testid="debtor-name-input" />
              @if (
                addDebtorForm.controls.name.touched &&
                addDebtorForm.controls.name.hasError('required')
              ) {
                <mat-error>Required</mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Registration number (optional)</mat-label>
              <input matInput formControlName="reg_no" />
            </mat-form-field>
            <div class="form__actions">
              <button
                mat-stroked-button
                type="submit"
                [disabled]="isAddingDebtor()"
                data-testid="add-debtor-btn"
              >
                @if (isAddingDebtor()) {
                  <mat-spinner diameter="20" />
                } @else {
                  <span>Add debtor</span>
                }
              </button>
            </div>
          </form>
        </mat-card-content>
      </mat-card>

      <mat-card appearance="outlined">
        <mat-card-content>
          <h2 class="section-title">Invoices</h2>

          @if (invoices().length === 0) {
            <div class="sg-empty">
              <app-icon name="document" [size]="40" />
              <p class="sg-empty__title">No invoices yet</p>
              <p class="sg-empty__desc">Submit an invoice below once a debtor is added.</p>
            </div>
          } @else {
            <table mat-table [dataSource]="invoices()" class="data-table">
              <ng-container matColumnDef="number">
                <th mat-header-cell *matHeaderCellDef>Invoice #</th>
                <td mat-cell *matCellDef="let inv">{{ inv.invoice_number }}</td>
              </ng-container>
              <ng-container matColumnDef="debtor">
                <th mat-header-cell *matHeaderCellDef>Debtor</th>
                <td mat-cell *matCellDef="let inv">{{ inv.debtor_name }}</td>
              </ng-container>
              <ng-container matColumnDef="amount">
                <th mat-header-cell *matHeaderCellDef>Amount</th>
                <td mat-cell *matCellDef="let inv">{{ inv.invoice_amount | zar }}</td>
              </ng-container>
              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Status</th>
                <td mat-cell *matCellDef="let inv">
                  <app-status-chip [status]="inv.invoice_status" />
                </td>
              </ng-container>
              <tr mat-header-row *matHeaderRowDef="['number', 'debtor', 'amount', 'status']"></tr>
              <tr
                mat-row
                *matRowDef="let inv; columns: ['number', 'debtor', 'amount', 'status']"
              ></tr>
            </table>
          }

          @if (debtors().length > 0) {
            <form [formGroup]="addInvoiceForm" (ngSubmit)="addInvoice()" class="inline-form">
              <h3 class="sub-section-title">Submit invoice</h3>
              <mat-form-field appearance="outline">
                <mat-label>Debtor</mat-label>
                <mat-select formControlName="relationship" data-testid="debtor-select">
                  @for (rel of debtors(); track rel.short_id) {
                    <mat-option [value]="rel.short_id">{{ rel.debtor.legal_name }}</mat-option>
                  }
                </mat-select>
                @if (
                  addInvoiceForm.controls.relationship.touched &&
                  addInvoiceForm.controls.relationship.hasError('required')
                ) {
                  <mat-error>Select a debtor</mat-error>
                }
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Invoice number</mat-label>
                <input matInput formControlName="invoice_number" />
                @if (
                  addInvoiceForm.controls.invoice_number.touched &&
                  addInvoiceForm.controls.invoice_number.hasError('required')
                ) {
                  <mat-error>Required</mat-error>
                }
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Invoice amount (ZAR)</mat-label>
                <input matInput type="number" formControlName="invoice_amount" min="1" />
                @if (
                  addInvoiceForm.controls.invoice_amount.touched &&
                  addInvoiceForm.controls.invoice_amount.hasError('required')
                ) {
                  <mat-error>Required</mat-error>
                }
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Invoice date (optional)</mat-label>
                <input matInput type="date" formControlName="invoice_date" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Due date (optional)</mat-label>
                <input matInput type="date" formControlName="due_date" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>If it looks overdue, why it is not (optional)</mat-label>
                <textarea
                  matInput
                  rows="2"
                  maxlength="1000"
                  formControlName="apparent_overdue_reason"
                ></textarea>
                <mat-hint>For example, the agreed payment terms.</mat-hint>
              </mat-form-field>
              <div class="inline-form__file">
                <label for="invoice-file">The invoice (PDF or image)</label>
                <input
                  id="invoice-file"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  (change)="pickInvoiceFile($event)"
                  data-testid="invoice-file"
                />
                @if (invoiceFileMissing()) {
                  <span class="field-error" role="alert">Attach the invoice before submitting it.</span>
                }
              </div>
              <div class="form__actions">
                <button
                  mat-stroked-button
                  type="submit"
                  [disabled]="isAddingInvoice()"
                  data-testid="add-invoice-btn"
                >
                  @if (isAddingInvoice()) {
                    <mat-spinner diameter="20" />
                  } @else {
                    <span>Submit invoice</span>
                  }
                </button>
              </div>
            </form>
          }
        </mat-card-content>
      </mat-card>
    }
  }
</div>
